// self-reflection.js - the COACH side of post-match self-reflection, reached
// from the Coach Hub. A coach opens a reflection for one match on their team,
// then reads each player's answers and either comments or just marks it seen.
// Player answers are read-only here (the player owns them, RLS enforces it);
// the coach's words live in a separate review row.
//
// Pair module: player-page.js renders the questionnaire + shows this feedback
// back to the player. Shared question catalogue: self-reflection-data.js.
// STANDALONE developmental lens - never a Dragons Score input.

import { AUTH } from './auth.js';
import { CONFIG } from './config.js';
import { DB } from './db.js';
import { DICTATION } from './dictation.js';
import {
  bandForAgeGroup, bandTitle, summarizeAnswers,
} from './self-reflection-data.js';
import { t } from './strings.js';
import { UI } from './ui.js';
import { UTILS } from './utils.js';

export const SELF_REFLECTION = (() => {

  let _team = null;
  let _onBack = null;
  let _season = null;
  let _requests = [];
  let _lineups = [];
  let _req = null;        // request open in the review screen
  let _subs = [];         // its submissions
  let _roster = [];       // team roster (to show who has not reflected)

  // ── Team home: the team's reflections + a "new reflection" form ─────────────

  async function showTeam(team, onBack) {
    _team = team; _onBack = onBack;
    _season = CONFIG.season.current;
    UI.showScreen('screen-reflect');
    document.getElementById('reflect-title').textContent = t('reflect.title');
    document.getElementById('reflect-sub').textContent = t('reflect.forTeam', { team: team.id });
    UI.setLoading('reflect-content', t('common.loading'));
    try {
      const [requests, lineups] = await Promise.all([
        DB.reflections.requestsForTeam(team.id, _season),
        DB.lineups.listForTeam(team.id, _season).catch(() => []),
      ]);
      _requests = requests || [];
      _lineups  = lineups  || [];
      _renderList();
    } catch (err) {
      console.error('Reflections load failed:', err);
      UI.setEmpty('reflect-content', t('common.error'));
    }
  }

  function backFromList() { if (_onBack) _onBack(); }
  function backFromReview() { showTeam(_team, _onBack); }

  function _band() { return bandForAgeGroup(_team?.age_group); }

  function _renderList() {
    const el = document.getElementById('reflect-content');
    if (!el) return;
    const esc = UTILS.escapeHtml;
    const todayIso = new Date().toISOString().slice(0, 10);

    // Recent line-ups let the coach open a reflection tied to a real game in
    // one tap (prefills date + opponent).
    const lineupChips = _lineups.slice(0, 6).map(l =>
      `<button class="sel-section-toggle" data-lu="${esc(l.id)}"
        data-date="${esc(l.match_date)}" data-opp="${esc(l.opponent || '')}">
        ${esc(UTILS.formatDate(l.match_date, 'short'))}${l.opponent ? ' v ' + esc(l.opponent) : ''}
      </button>`).join('');

    const form = `
      <div class="sel-detail-section" style="margin-bottom:18px;">
        <div class="sel-detail-section__title">${esc(t('reflect.newTitle'))}</div>
        <div class="sel-detail-hint" style="margin-bottom:10px;">
          ${esc(t('reflect.usesSet', { set: bandTitle(_band(), CONFIG.i18n?.lang || 'en') }))}
        </div>
        ${_lineups.length ? `
          <div class="gate__label">${esc(t('reflect.fromLineup'))}</div>
          <div class="sel-section-toggles" style="margin-bottom:10px;">${lineupChips}</div>` : ''}
        <div class="gate__label">${esc(t('reflect.matchDate'))}</div>
        <input type="date" class="prompt-input" id="reflect-date" value="${todayIso}" max="${todayIso}">
        <div class="gate__label" style="margin-top:10px;">${esc(t('reflect.opponent'))}</div>
        <input type="text" class="prompt-input" id="reflect-opp" placeholder="${esc(t('reflect.opponentHint'))}">
        <div class="gate__label" style="margin-top:10px;">${esc(t('reflect.message'))}</div>
        <textarea class="prompt-input" id="reflect-msg" rows="2" placeholder="${esc(t('reflect.messageHint'))}"></textarea>
        <button class="btn btn--primary" id="reflect-create" style="margin-top:12px;">${esc(t('reflect.open'))}</button>
      </div>`;

    const list = _requests.length
      ? _requests.map(r => {
          const n = r.self_evals?.[0]?.count || 0;
          return `
            <button class="player-eval-card" data-req="${esc(r.id)}">
              <div class="player-eval-card__main">
                <div class="player-eval-card__title">${esc(UTILS.formatDate(r.match_date, 'medium'))}${r.opponent ? ' · ' + esc(r.opponent) : ''}</div>
                <div class="player-eval-card__sub">${esc(t('reflect.reflectedCount', { n }))}${r.closed_at ? ' · ' + esc(t('reflect.closed')) : ''}</div>
              </div>
              <span class="player-eval-card__chevron">›</span>
            </button>`;
        }).join('')
      : `<div class="sel-detail-hint">${esc(t('reflect.none'))}</div>`;

    el.innerHTML = form + `<div class="sel-detail-section__title" style="margin:6px 0 8px;">${esc(t('reflect.pastTitle'))}</div>` + list;

    document.getElementById('reflect-create')?.addEventListener('click', _create);
    el.querySelectorAll('[data-lu]').forEach(b => b.addEventListener('click', () => {
      const d = document.getElementById('reflect-date'); if (d) d.value = b.dataset.date || todayIso;
      const o = document.getElementById('reflect-opp'); if (o) o.value = b.dataset.opp || '';
      b.classList.add('sel-section-toggle--on');
    }));
    el.querySelectorAll('[data-req]').forEach(b =>
      b.addEventListener('click', () => _openReview(b.dataset.req)));
  }

  async function _create() {
    const date = document.getElementById('reflect-date')?.value;
    if (!date) { UI.toast(t('reflect.needDate'), 'error'); return; }
    const opp = (document.getElementById('reflect-opp')?.value || '').trim();
    const msg = (document.getElementById('reflect-msg')?.value || '').trim();
    // Match a chosen line-up chip back to its id (for the optional tie-in).
    const activeChip = document.querySelector('#reflect-content .sel-section-toggle--on[data-lu]');
    const btn = document.getElementById('reflect-create');
    if (btn) btn.disabled = true;
    try {
      const row = await DB.reflections.createRequest({
        team_id:   _team.id,
        season_id: _season,
        match_date: date,
        opponent:  opp || null,
        band:      _band(),
        message:   msg || null,
        lineup_id: activeChip?.dataset.lu || null,
        created_by: AUTH.getStaffId(),
      });
      UI.toast(t('reflect.opened'), 'success', 1500);
      // The new row has to join the cached list before the review opens.
      // _openReview looks the request up there for its date and opponent, so
      // without this the header reads "Reflections - " with nothing after the
      // dash until you go back and come in again.
      if (row?.id) { _requests = [row, ..._requests]; _openReview(row.id); }
      else showTeam(_team, _onBack);
    } catch (err) {
      console.error('Create reflection failed:', err);
      UI.toast(t('common.error'), 'error');
      if (btn) btn.disabled = false;
    }
  }

  // ── Review one reflection: each player's answers + coach feedback ───────────

  async function _openReview(requestId) {
    _req = _requests.find(r => r.id === requestId) || { id: requestId };
    UI.showScreen('screen-reflect-review');
    document.getElementById('reflect-review-title').textContent =
      t('reflect.reviewTitle', { date: UTILS.formatDate(_req.match_date, 'medium') });
    document.getElementById('reflect-review-sub').textContent =
      _req.opponent ? _req.opponent : '';
    UI.setLoading('reflect-review-content', t('common.loading'));
    try {
      const [subs, roster] = await Promise.all([
        DB.reflections.submissionsForRequest(requestId),
        DB.players.getByTeam(_team.id, _season).catch(() => []),
      ]);
      _subs   = subs   || [];
      _roster = (roster || []).map(m => ({ id: m.players.id, name: m.players.name }));
      _renderReview();
    } catch (err) {
      console.error('Reflection review load failed:', err);
      UI.setEmpty('reflect-review-content', t('common.error'));
    }
  }

  function _renderReview() {
    const el = document.getElementById('reflect-review-content');
    if (!el) return;
    const esc = UTILS.escapeHtml;
    const lang = CONFIG.i18n?.lang || 'en';
    const band = _req.band || _band();

    const submittedIds = new Set(_subs.map(s => s.player_id));
    const waiting = _roster.filter(p => !submittedIds.has(p.id));

    const cards = _subs.length ? _subs.map(s => {
      const review = s.self_eval_reviews?.[0] || null;
      const rows = summarizeAnswers(band, s.answers || {}, lang);
      const name = s.players?.name || t('reflect.aPlayer');
      const answersHtml = rows.map(r => `
        <div class="eval-row">
          <div><div class="eval-row__skill">${esc(r.label)}</div></div>
          <div class="reflect-answer">${esc(r.value)}</div>
        </div>`).join('') || `<div class="sel-detail-hint">${esc(t('reflect.blank'))}</div>`;
      return `
        <div class="sel-detail-section reflect-card" data-eval="${esc(s.id)}">
          <div class="reflect-card__head">
            <div class="sel-detail-section__title" style="margin:0;">${esc(name)}</div>
            ${review
              ? `<span class="player-read-chip">${esc(review.comment ? t('reflect.commented') : t('reflect.seen'))}</span>`
              : `<span class="player-unread-chip">${esc(t('reflect.newBadge'))}</span>`}
          </div>
          ${answersHtml}
          <div class="gate__label" style="margin-top:10px;">${esc(t('reflect.yourFeedback'))}</div>
          <textarea class="prompt-input reflect-fb" data-fb="${esc(s.id)}" rows="3"
            placeholder="${esc(t('reflect.feedbackHint'))}">${esc(review?.comment || '')}</textarea>
          <div class="reflect-card__actions">
            <button class="btn btn--primary" data-save-fb="${esc(s.id)}">${esc(t('reflect.saveFeedback'))}</button>
            <button class="btn btn--ghost" data-seen="${esc(s.id)}">${esc(t('reflect.markSeen'))}</button>
          </div>
        </div>`;
    }).join('') : `<div class="sel-detail-hint">${esc(t('reflect.noSubs'))}</div>`;

    const waitingHtml = waiting.length
      ? `<div class="sel-detail-section"><div class="sel-detail-section__title">${esc(t('reflect.waiting'))}</div>
          <div class="sel-detail-hint">${esc(waiting.map(p => p.name).join(', '))}</div></div>`
      : '';

    const admin = `
      <div class="reflect-card__actions" style="margin-top:14px;">
        <button class="btn btn--secondary" id="reflect-toggle-closed">
          ${esc(_req.closed_at ? t('reflect.reopen') : t('reflect.close'))}</button>
        <button class="btn btn--ghost" id="reflect-delete">${esc(t('reflect.delete'))}</button>
      </div>`;

    el.innerHTML =
      (_req.message ? `<div class="eval-meta">${esc(_req.message)}</div>` : '') +
      cards + waitingHtml + admin;

    // Speak-don't-type on every feedback box (same pipeline coaches already use).
    el.querySelectorAll('.reflect-fb').forEach(ta => DICTATION.attach(ta, {}));

    el.querySelectorAll('[data-save-fb]').forEach(b =>
      b.addEventListener('click', () => _saveFeedback(b.dataset.saveFb)));
    el.querySelectorAll('[data-seen]').forEach(b =>
      b.addEventListener('click', () => _saveFeedback(b.dataset.seen, true)));
    document.getElementById('reflect-toggle-closed')?.addEventListener('click', _toggleClosed);
    document.getElementById('reflect-delete')?.addEventListener('click', _deleteRequest);
  }

  async function _saveFeedback(selfEvalId, seenOnly) {
    const ta = document.querySelector(`.reflect-fb[data-fb="${selfEvalId}"]`);
    const comment = seenOnly ? '' : (ta?.value || '');
    try {
      await DB.reflections.saveReview(selfEvalId, AUTH.getStaffId(), comment);
      // Reflect the new review locally so the chip updates without a refetch.
      const s = _subs.find(x => x.id === selfEvalId);
      if (s) s.self_eval_reviews = [{ comment: comment.trim() || null, reviewed_at: new Date().toISOString() }];
      UI.toast(seenOnly ? t('reflect.seenToast') : t('reflect.feedbackSaved'), 'success', 1400);
      _renderReview();
    } catch (err) {
      console.error('Save feedback failed:', err);
      UI.toast(t('common.error'), 'error');
    }
  }

  async function _toggleClosed() {
    try {
      await DB.reflections.setClosed(_req.id, !_req.closed_at);
      _req.closed_at = _req.closed_at ? null : new Date().toISOString();
      const r = _requests.find(x => x.id === _req.id); if (r) r.closed_at = _req.closed_at;
      _renderReview();
    } catch (err) {
      console.error('Toggle closed failed:', err);
      UI.toast(t('common.error'), 'error');
    }
  }

  function _deleteRequest() {
    UI.modal({
      title: t('reflect.deleteTitle'),
      body:  t('reflect.deleteBody'),
      buttons: [
        { label: t('common.cancel') },
        { label: t('reflect.delete'), style: 'danger', action: async () => {
          try {
            await DB.reflections.deleteRequest(_req.id);
            showTeam(_team, _onBack);
          } catch (err) {
            console.error('Delete reflection failed:', err);
            UI.toast(t('common.error'), 'error');
          }
        } },
      ],
    });
  }

  return { showTeam, backFromList, backFromReview };

})();
